import React, { useEffect, useRef, useState } from "react";
import { Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import axios from "axios";

// Register required Chart.js components
Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const CombinedBarChart = () => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const [publicationData, setPublicationData] = useState({});
    const [projectData, setProjectData] = useState({});
    const [moudata1, setMoudata1] = useState({});
    const [patentdata, setPatentData] = useState([]);  // Store patent data
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch department data
    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL || 'https://ooo-toddler-gifts-pubs.trycloudflare.como.trycloudflare.com'}/api/departments`);
                setDepartments(response.data);  // Set department data in state
                setLoading(false);
            } catch (err) {
                setError("Failed to fetch department data.");
                setLoading(false);
            }
        };
        fetchDepartments();
    }, []);

    // Fetch MOU data
    useEffect(() => {
        const fetchMoudata = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/mous/department-publications`);
                setMoudata1(response.data);
                setLoading(false);
            } catch (err) {
                setError("Failed to fetch MOU data.");
                setLoading(false);
            }
        };
        fetchMoudata();
    }, []);

    // Fetch publication data
    useEffect(() => {
        const fetchPublicationData = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/publications/department-publications`);
                setPublicationData(response.data);
                setLoading(false);
            } catch (err) {
                setError("Failed to fetch publication data.");
                setLoading(false);
            }
        };
        fetchPublicationData();
    }, []);

    // Fetch patent data
    useEffect(() => {
        const fetchPatentData = async () => {
            try {
                const firstResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/patents`);
                const size = firstResponse.data.page.totalElements;
    
                // Now fetch all the patents based on the total number of elements
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/patents?&size=${size}`);
    
                if (response.data && response.data._embedded && response.data._embedded.patents) {
                    const patents = response.data._embedded.patents;
                    setPatentData(patents);

                    // Count patents by department
                    const departmentCounts = patents.reduce((acc, patent) => {
                        const departmentCode = patent.department.split(' ')[0]; // Extracting the department code
                        const departmentName = departments.find(dept => dept.deptId === departmentCode)?.deptName || patent.department;
                        acc[departmentName] = (acc[departmentName] || 0) + 1;
                        return acc;
                    }, {});
                    
                    setLoading(false);
                } else {
                    setError("No patents found.");
                    setLoading(false);
                }
            } catch (err) {
                setError("Failed to fetch patent data.");
                setLoading(false);
            }
        };
        fetchPatentData();
    }, [departments]);  // Dependency on departments to ensure it fetches after department data is available

    // Fetch project data
    useEffect(() => {
        const fetchProjectData = async () => {
            try {
                const validDepartments = departments.filter(dept => dept && dept.deptId);
                const responses = await Promise.all(
                    validDepartments.map(dept =>
                        axios.get(`${process.env.REACT_APP_API_BASE_URL || 'https://ooo-toddler-gifts-pubs.trycloudflare.como.trycloudflare.com'}/api/project-departments/project-type-count/${dept.deptId}`)
                    )
                );
                const allProjectData = {};
                responses.forEach((response, index) => {
                    allProjectData[validDepartments[index].deptName] = response.data;
                });
                setProjectData(allProjectData);
                setLoading(false);
            } catch (err) {
                setError("Failed to fetch project data.");
            }
        };

        if (departments.length > 0) {
            fetchProjectData();
        }
    }, [departments]);  // Fetch project data after departments are available



    const fetchTotalProjectCount = async () => {
        try {
            // Step 1: Fetch all departments
            const departmentResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL || 'https://ooo-toddler-gifts-pubs.trycloudflare.como.trycloudflare.com'}/api/departments`);
            const validDepartments = departmentResponse.data.filter(dept => dept && dept.deptId);
    
            // Step 2: Fetch project counts for each department
            const responses = await Promise.all(
                validDepartments.map(dept =>
                    axios.get(`${process.env.REACT_APP_API_BASE_URL || 'https://ooo-toddler-gifts-pubs.trycloudflare.como.trycloudflare.com'}/api/project-departments/project-type-count/${dept.deptId}`)
                )
            );
    
            // Step 3: Calculate total project count by summing all values for each department
            let totalCount = 0;
            responses.forEach((response, index) => {
                console.log(`Response for ${validDepartments[index].deptName}:`, response.data);
    
                // Sum all counts in the response object
                const departmentCounts = response.data;
                for (const key in departmentCounts) {
                    if (departmentCounts.hasOwnProperty(key)) {
                        const count = departmentCounts[key];
                        totalCount += typeof count === 'number' ? count : 0;  // Add only if the value is a number
                    }
                }
            });
    
            console.log("Total Project Count:", totalCount);
            return totalCount;  // Return the total project count
        } catch (error) {
            console.error("Failed to fetch total project count:", error);
            throw new Error("Failed to fetch total project count.");
        }
    };
    
    
 

    useEffect(() => {
        fetchTotalProjectCount()
        .then(totalCount => console.log("Total Project Count:1", totalCount))
        .catch(error => console.error(error.message));
    }, []);


    // Filter patent data by department and return department counts
    const departmentCounts = patentdata.reduce((acc, patent) => {
        const departmentCode = patent.department.split(' ')[0]; // Assuming '101 CIVIL'
        const departmentName = departments.find(dept => dept.deptId === departmentCode)?.deptName || patent.department;

        acc[departmentName] = (acc[departmentName] || 0) + 1;
        return acc;
    }, {});

    const departmentsList = Object.keys(departmentCounts);
    const patentCounts = Object.values(departmentCounts);

    // Render the chart
    useEffect(() => {
        if (loading || error || !departments.length) return;

        const renderChart = () => {
            const ctx = chartRef.current.getContext("2d");

            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }

            // Create gradients for bar colors
            const createGradient = (ctx, color1, color2) => {
                const gradient = ctx.createLinearGradient(0, 0, 0, 400);
                gradient.addColorStop(0, color1);
                gradient.addColorStop(1, color2);
                return gradient;
            };

            // Prepare the labels (department names)
            const labels = departments.map(dept => dept.deptName);

            // Prepare data arrays (for each metric)
            const publications = labels.map(deptName => publicationData[deptName] || 0);
            const projects = labels.map(deptName => {
                const projectCount = projectData[deptName]
                    ? Object.values(projectData[deptName]).reduce((a, b) => a + b, 0)
                    : 0;
                return projectCount;
            });
            const mous = labels.map(deptName => moudata1[deptName] || 0);

            // Patent Count logic: count patents for each department
            const patentsCount = labels.map(deptName => {
                const departmentIndex = departmentsList.indexOf(deptName);
                return departmentIndex !== -1 ? patentCounts[departmentIndex] : 0;
            });

            // Filter out departments where all counts are zero
            const filteredLabels = labels.filter((label, index) => {
                return (
                    publications[index] !== 0 ||
                    projects[index] !== 0 ||
                    mous[index] !== 0 ||
                    patentsCount[index] !== 0
                );
            });

            // Filter corresponding data arrays
            const filteredPublications = filteredLabels.map(label => publications[labels.indexOf(label)]);
            const filteredProjects = filteredLabels.map(label => projects[labels.indexOf(label)]);
            const filteredMous = filteredLabels.map(label => mous[labels.indexOf(label)]);
            const filteredPatents = filteredLabels.map(label => patentsCount[labels.indexOf(label)]);

           // Create merged data for the chart
const mergedData = {
    labels: filteredLabels,
    datasets: [
      {
        label: "Project Count",
        data: filteredProjects,
        backgroundColor: createGradient(ctx, "#4C9FFF", "#A7C7E7"),
        borderColor: "rgba(0, 0, 0, 0.1)", // Very light border color
        borderWidth: 1,
        barThickness: 25
      },
      {
        label: "Publication Count",
        data: filteredPublications,
        backgroundColor: createGradient(ctx, "#FF6F61", "#FFB6B9"),
        borderColor: "rgba(0, 0, 0, 0.1)", // Very light border color
        borderWidth: 1,
        barThickness: 25
      },
      {
        label: "MOU Count",
        data: filteredMous,
        backgroundColor: createGradient(ctx, "#20c997", "#17a2b8"),
        borderColor: "rgba(0, 0, 0, 0.1)", // Very light border color
        borderWidth: 1,
        barThickness: 25
      },
      {
        label: "Patent Count",
        data: filteredPatents,
        backgroundColor: createGradient(ctx, "#FFB74D", "#FFEB8E"),
        borderColor: "rgba(0, 0, 0, 0.1)", // Very light border color
        borderWidth: 1,
        barThickness: 25
      }
    ]
  };
  
  // Create the chart
  chartInstanceRef.current = new Chart(ctx, {
    type: "bar",
    data: mergedData,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          stacked: true,
          grid: {
            color: "rgba(0, 0, 0, 0.05)" // Very light grid lines for subtle effect
          },
          ticks: {
            color: "#333" // Darker tick marks for readability
          }
        },
        x: {
          stacked: true,
          grid: {
            color: "rgba(0, 0, 0, 0.05)" // Very light grid lines
          },
          ticks: {
            color: "#333" // Darker tick marks for readability
          }
        }
      },
      plugins: {
        tooltip: {
          backgroundColor: "#ffffff", // White tooltip background
          borderColor: "rgba(0, 0, 0, 0.3)", // Light black border for tooltips
          borderWidth: 1,
          bodyColor: "#333", // Dark text color for readability
          titleColor: "#007bff", // Professional blue title color
          padding: 10, // Extra padding for a more spacious look
          callbacks: {
            label: function (context) {
              return `${context.dataset.label}: ${context.raw}`;
            }
          }
        },
        legend: {
          labels: {
            color: "#333", // Professional text color for legend
            font: {
              size: 14, // Slightly larger font for readability
              family: "'Verdana', sans-serif"
            }
          }
        }
      },
      layout: {
        padding: {
          top: 20, // Add space at the top
          bottom: 20 // Add space at the bottom
        }
      }
    }
  });
  
        };

        renderChart();

        // Cleanup the chart instance on unmount or dependency change
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, [loading, error, departments, publicationData, projectData, moudata1, patentdata]);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div>
            {/* Bar Chart */}
            <div style={{ height: "370px", width: "100%" }}>
                <canvas ref={chartRef}></canvas>
            </div>
        </div>
    );
};

export default CombinedBarChart;
